/**
 * usePWAInstall — Hook qui expose l'état d'installation PWA.
 *
 * Retourne :
 *  - canInstall : bool (true sur Chrome/Edge/Brave quand beforeinstallprompt a fired)
 *  - isStandalone : bool (déjà installée)
 *  - isIos : bool (iPhone / iPad, install via menu Partager uniquement)
 *  - isInAppBrowser : bool (navigateur intégré à une app, installation impossible)
 *  - promptInstall() : déclenche le prompt natif si canInstall
 */
import { useEffect, useState } from "react";

function _isStandalone() {
  return (
    typeof window !== "undefined" &&
    (window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true)
  );
}

function _isIos() {
  if (typeof window === "undefined") return false;
  const nav = window.navigator;
  // Depuis iPadOS 13, l'iPad se présente comme un Mac : on le reconnaît à son écran tactile.
  const isIpadOs = /Macintosh/.test(nav.userAgent) && nav.maxTouchPoints > 1;
  return (/iPad|iPhone|iPod/.test(nav.userAgent) || isIpadOs) && !window.MSStream;
}

/** Navigateur intégré à une app (Facebook, Instagram, Messenger…) : l'installation y est impossible. */
function _isInAppBrowser() {
  return typeof window !== "undefined" && /FBAN|FBAV|Instagram|Messenger|Line\/|LinkedInApp|Snapchat|TikTok/i.test(window.navigator.userAgent);
}

export function usePWAInstall() {
  const [deferred, setDeferred] = useState(null);
  const [isStandalone, setIsStandalone] = useState(_isStandalone());
  const [isIos] = useState(_isIos());
  const [isInAppBrowser] = useState(_isInAppBrowser());

  useEffect(() => {
    if (isStandalone) return;
    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    window.addEventListener("beforeinstallprompt", handler);
    window.addEventListener("appinstalled", () => setIsStandalone(true));
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, [isStandalone]);

  async function promptInstall() {
    if (!deferred) return { outcome: "no-prompt" };
    deferred.prompt();
    const choice = await deferred.userChoice;
    setDeferred(null);
    return choice;
  }

  return {
    canInstall: !!deferred,
    isStandalone,
    isIos,
    isInAppBrowser,
    promptInstall,
  };
}
